<template>
  <li
    class="row"
    :class="{ 'archived-task': task.archived }"
    :style="rowStyle"
    :data-task-id="rowDataTaskId || null"
    @click="handleCardClick"
    @mousedown="handleMouseDown"
  >
    <!-- First line: Title (left) + Status + Transition button (right) -->
    <div class="task-header">
      <div class="task-title-section">
        <div ref="titleElement" class="task-title-wrapper" :class="{ expanded: titleExpanded }">
          <span
            class="task-title"
            :class="{ 'title-truncated': isTitleTruncated && !titleExpanded }"
            >{{ task.title }}</span
          >
        </div>
        <button
          v-if="isTitleTruncated"
          class="btn-icon title-expand-btn"
          :title="titleExpanded ? s__('TaskRow|Collapse') : s__('TaskRow|Expand')"
          @click.stop="titleExpanded = !titleExpanded"
        >
          <ChevronDownIcon :size="16" :class="{ rotated: titleExpanded }" />
        </button>
      </div>
      <div class="task-status-section">
        <div class="status-wrapper">
          <span v-if="task.workflowStatus" class="status-indicator">
            <span class="status-dot" :style="{ backgroundColor: statusColor }"></span>
            <span class="status-name">{{ task.workflowStatus.name }}</span>
          </span>
          <span v-else class="muted">—</span>
          <button
            v-if="task.workflowStatus && !task.archived"
            class="transition-btn btn-xs"
            :title="s__('TaskRow|Change status')"
            @click.stop="openTransitionModal"
            :disabled="loadingTransitions || executingTransition"
          >
            <TransitionIcon v-if="!loadingTransitions" />
            <span v-else class="loading-dots">⋯</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Second line: Two content columns -->
    <div class="task-content">
      <div class="content-column content-left">
        <div v-if="task.assignee" class="muted task-meta-text">
          {{ s__('TaskRow|Assignee:') }} {{ person(task.assignee) }}
          <span v-if="assigneeProjectRole" class="task-meta-project-role">
            ({{ assigneeProjectRole }})
          </span>
        </div>
        <div v-if="task.author" class="muted task-meta-text">
          {{ s__('TaskRow|Author:') }} {{ person(task.author) }}
          <span v-if="authorProjectRole" class="task-meta-project-role">
            ({{ authorProjectRole }})
          </span>
        </div>
        <div v-if="showProjectMeta" class="muted task-meta-text">
          {{ s__('TaskRow|Project:') }} {{ task.project.title }}
        </div>
      </div>
      <div class="content-column content-right muted">
        <div v-if="task.dueAt" class="task-meta-text">
          {{ s__('TaskRow|Due:') }}
          <formatted-date-time :value="task.dueAt" :locale="locale" :timezone="timezone" />
        </div>
        <div v-if="task.finishedAt" class="task-meta-text">
          {{ s__('TaskRow|Finished:') }}
          <formatted-date-time :value="task.finishedAt" :locale="locale" :timezone="timezone" />
        </div>
        <div v-if="task.priority" class="task-meta-text priority-meta">
          <PriorityIcon :priority="task.priority" :size="14" />
          <span style="margin-left: 4px">{{ translatePriority(task.priority) }}</span>
        </div>
      </div>
    </div>

    <!-- Third line: Personal labels (if any) -->
    <div
      v-if="task.personalLabels && task.personalLabels.length > 0"
      class="labels-container personal-labels"
    >
      <LabelBadge
        v-for="personalLabel in task.personalLabels"
        :key="'personal-' + personalLabel.id"
        :label="personalLabel"
        personal
        clickable
        @click-label="openPersonalLabelTasksModal"
      />
    </div>

    <!-- Fourth line: Project labels -->
    <div
      v-if="(task.labels && task.labels.length > 0) || task.archived"
      class="labels-container project-labels"
    >
      <span v-if="task.archived" class="label-badge archived-label">
        {{ s__('TaskRow|Archived') }}
      </span>
      <LabelBadge
        v-for="label in task.labels"
        :key="label.id"
        :label="labelForModal(label)"
        clickable
        @click-label="openLabelTasksModal"
      />
    </div>
  </li>

  <TransitionSelectionModal
    :visible="showTransitionModal"
    :transitions="availableTransitions"
    :task="taskWithChecklist"
    :checklists-enabled="effectiveChecklistsEnabled"
    :executing="executingTransition"
    :loading="loadingTransitions"
    @confirm="confirmTransition"
    @cancel="closeTransitionModal"
  />
  <LabelTasksModal
    :visible="!!selectedLabel"
    :label="selectedLabel"
    :scope="selectedLabelScope"
    :project-id="selectedLabel?.projectId || selectedLabel?.project_id || task.project?.id"
    :chat-id="chatId"
    :from-id="fromId"
    @label-click="openScopedLabelTasksModal"
    @close="selectedLabel = null"
  />
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, translatePriority } from '@/locale'
import FormattedDateTime from '../common/FormattedDateTime.vue'
import TransitionSelectionModal from '../boards/TransitionSelectionModal.vue'
import TransitionIcon from '../common/icons/TransitionIcon.vue'
import ChevronDownIcon from '../common/icons/ChevronDownIcon.vue'
import PriorityIcon from '../common/icons/PriorityIcon.vue'
import LabelBadge from '../common/LabelBadge.vue'
import LabelTasksModal from '../common/LabelTasksModal.vue'
import { getProjectRole } from '@/utils/projectRoles'
import { navigateToUrl, handleMiddleMouseClick } from '@/utils/navigation'

const props = defineProps({
  task: { type: Object, required: true },
  rowDataTaskId: { type: String, default: null },
  dashboardScope: {
    type: String,
    default: 'personal',
    validator: value => ['personal', 'project'].includes(value),
  },
  projectRolesMap: { type: Map, default: () => new Map() },
  chatId: String,
  fromId: String,
  timezone: { type: String, default: null },
  locale: { type: String, default: null },
  checklistsEnabled: { type: Boolean, default: false },
})

const emit = defineEmits(['transition-executed'])

const titleExpanded = ref(false)
const titleElement = ref(null)
const isTitleTruncated = ref(false)
const selectedLabel = ref(null)
const selectedLabelScope = ref('project')

function person(u) {
  return u?.username ? '@' + u.username : u?.name || (u ? s__('TaskRow|User') + ' ' + u.id : '—')
}

// Check if title is truncated
const checkTitleTruncation = () => {
  nextTick(() => {
    if (titleElement.value && !titleExpanded.value) {
      const element = titleElement.value.querySelector('.task-title')
      if (element) {
        isTitleTruncated.value = element.scrollWidth > element.clientWidth
      }
    }
  })
}

onMounted(() => {
  checkTitleTruncation()
  window.addEventListener('resize', checkTitleTruncation)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkTitleTruncation)
})

watch(
  () => props.task.title,
  () => {
    titleExpanded.value = false
    checkTitleTruncation()
  }
)

watch(titleExpanded, () => {
  if (!titleExpanded.value) {
    checkTitleTruncation()
  }
})

const assigneeProjectRole = computed(() => {
  if (!props.task.assignee?.id) return null
  return getProjectRole(props.projectRolesMap, props.task.assignee.id)
})

const authorProjectRole = computed(() => {
  if (!props.task.author?.id) return null
  return getProjectRole(props.projectRolesMap, props.task.author.id)
})

const showProjectMeta = computed(() => {
  if (!props.task.project) return false
  return props.dashboardScope !== 'project'
})

const taskUrl = computed(() => {
  const projectSegment = props.task.project.slug || props.task.project.id
  const pid = encodeURIComponent(projectSegment)
  const taskSegment = props.task.iid
  const tid = encodeURIComponent(taskSegment)
  const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
  const from = props.fromId ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}` : ''
  return `/projects/${pid}/tasks/${tid}${chat}${from}`
})

const taskResolverId = computed(() => props.task.id)

// Store current URL (with filters) when navigating to task detail page
function storeCurrentUrl() {
  try {
    const projectSegment = props.task.project.slug || props.task.project.id
    // Store the current page URL (which includes filters in query params)
    const currentUrl = window.location.pathname + window.location.search
    // Store if we're on project task surfaces (legacy index or dashboard).
    if (
      currentUrl.match(/\/projects\/[^/]+\/tasks(\?|$)/) ||
      currentUrl.match(/\/projects\/[^/]+\/dashboard(\?|$)/)
    ) {
      sessionStorage.setItem(`taskIndexUrl_${projectSegment}`, currentUrl)
      // Clear dashboard URL to prevent conflicts
      sessionStorage.removeItem('dashboardUrl')
    }
    // Store if we're on the dashboard
    else if (currentUrl === '/dashboard' || currentUrl.startsWith('/dashboard?')) {
      sessionStorage.setItem('dashboardUrl', currentUrl)
      // Clear project tasks URLs to prevent conflicts
      // We can't clear all project URLs, but we can at least clear the one for this task's project
      sessionStorage.removeItem(`taskIndexUrl_${projectSegment}`)
    }
  } catch (e) {
    console.warn('Failed to store task index URL:', e)
  }
}

function handleCardClick(event) {
  // Don't navigate if clicking on the transition button or its children
  if (event.target.closest('.transition-btn') || event.target.closest('.label-badge-button')) {
    return
  }
  if (!taskResolverId.value) return

  navigateToUrl(taskUrl.value, event, {
    beforeNavigate: storeCurrentUrl,
  })
}

// Handle middle mouse button (button 1) which may not trigger click event
function handleMouseDown(event) {
  if (!taskResolverId.value) return
  handleMiddleMouseClick(taskUrl.value, event, {
    beforeNavigate: storeCurrentUrl,
    shouldNavigate: event =>
      !event.target.closest('.transition-btn') && !event.target.closest('.label-badge-button'),
  })
}

function labelForModal(label) {
  return {
    ...label,
    projectId: props.task.project?.id,
  }
}

function openLabelTasksModal(label) {
  selectedLabelScope.value = 'project'
  selectedLabel.value = label
}

function openPersonalLabelTasksModal(label) {
  selectedLabelScope.value = 'personal'
  selectedLabel.value = label
}

function openScopedLabelTasksModal(payload) {
  if (!payload?.label) return
  selectedLabelScope.value = payload.scope || 'project'
  selectedLabel.value = payload.label
}

const statusColor = computed(() => {
  return props.task.workflowStatus?.color || '#6B7280'
})

const rowStyle = computed(() => {
  const baseStyle = {
    borderLeft: `4px solid ${statusColor.value}`,
  }
  return baseStyle
})

// Transition modal state
const showTransitionModal = ref(false)
const availableTransitions = ref([])
const loadingTransitions = ref(false)
const executingTransition = ref(false)

// GraphQL Query for available transitions
const GET_TASK_AVAILABLE_TRANSITIONS = gql`
  query GetTaskAvailableTransitions($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      id
      checklistsEnabled
      task(id: $taskId) {
        id
        availableTransitions {
          id
          name
          targetStatus {
            id
            name
            color
            requiresCompletedChecklists
          }
        }
        checklist {
          id
          completed
        }
        checklistCompleted
      }
    }
  }
`

// GraphQL Mutation for executing transition
const EXECUTE_TRANSITION = gql`
  mutation ExecuteTransition($taskId: ID!, $transitionId: ID!) {
    executeTransition(taskId: $taskId, transitionId: $transitionId) {
      task {
        id
        workflowStatus {
          id
          name
          color
        }
        availableTransitions {
          id
          name
          targetStatus {
            id
            name
            color
            requiresCompletedChecklists
          }
        }
      }
      errors
    }
  }
`

// Variables ref for task transitions query
const taskTransitionsVars = computed(() => {
  if (!props.task?.project?.id || !taskResolverId.value) {
    return undefined
  }
  return {
    projectId: String(props.task.project.id),
    taskId: String(taskResolverId.value),
  }
})

const {
  load: loadTaskTransitions,
  result: taskTransitionsResult,
  loading: loadingTaskTransitionsQuery,
  refetch: refetchTaskTransitions,
} = useLazyQuery(GET_TASK_AVAILABLE_TRANSITIONS, () => taskTransitionsVars.value, {
  fetchPolicy: 'no-cache',
})

const { mutate: executeTransitionMutation } = useMutation(EXECUTE_TRANSITION)

// Watch loading state
watch(loadingTaskTransitionsQuery, loading => {
  loadingTransitions.value = loading
})

// Computed checklistsEnabled - use query result if available, otherwise use prop
const effectiveChecklistsEnabled = computed(() => {
  if (taskTransitionsResult.value?.project?.checklistsEnabled !== undefined) {
    return taskTransitionsResult.value.project.checklistsEnabled
  }
  return props.checklistsEnabled || false
})

// Computed task with checklist data from query result
const taskWithChecklist = computed(() => {
  const baseTask = { ...props.task }
  if (taskTransitionsResult.value?.project?.task) {
    const taskData = taskTransitionsResult.value.project.task
    // Merge checklist data from query result
    if (taskData.checklist !== undefined) {
      baseTask.checklist = taskData.checklist
    }
    if (taskData.checklistCompleted !== undefined) {
      baseTask.checklistCompleted = taskData.checklistCompleted
    }
  }
  return baseTask
})

// Watch for transitions result
watch(
  taskTransitionsResult,
  data => {
    if (data?.project?.task?.availableTransitions) {
      availableTransitions.value = data.project.task.availableTransitions
    } else {
      availableTransitions.value = []
    }
  },
  { immediate: true }
)

async function openTransitionModal() {
  showTransitionModal.value = true

  // Load transitions only when modal opens (lazy loading)
  try {
    if (taskTransitionsResult.value) {
      // If we've loaded transitions before, refetch to get latest state
      await refetchTaskTransitions()
    } else {
      // First time loading
      await loadTaskTransitions()
    }
  } catch (e) {
    console.error('Failed to load transitions:', e)
    availableTransitions.value = []
  }
}

function closeTransitionModal() {
  showTransitionModal.value = false
}

async function confirmTransition(transition) {
  if (!transition || !props.task?.id) return

  executingTransition.value = true

  try {
    const result = await executeTransitionMutation({
      taskId: String(props.task.id),
      transitionId: String(transition.id),
    })

    if (result?.data?.executeTransition?.errors?.length > 0) {
      const errorMessages = result.data.executeTransition.errors
      console.error('Transition execution errors:', errorMessages)
      // Could emit an error event here if needed
    } else if (result?.data?.executeTransition?.task) {
      // Emit event to parent to refresh task data
      emit('transition-executed', {
        taskId: props.task.id,
        newStatus: result.data.executeTransition.task.workflowStatus,
      })

      // Update available transitions from result
      if (result.data.executeTransition.task.availableTransitions) {
        availableTransitions.value = result.data.executeTransition.task.availableTransitions
      }

      // Close modal after successful transition
      closeTransitionModal()
    }
  } catch (error) {
    console.error('Failed to execute transition:', error)
  } finally {
    executingTransition.value = false
  }
}
</script>

<style src="../../styles/forms.css"></style>
<style scoped>
.row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 12px; /* rounded-xl */
  border: 1px solid #d1d5db; /* border-gray-300 */
  padding: 16px; /* p-6 */
  background-color: #f9fafb; /* bg-gray-50 */
  transition: border-color 0.15s ease-in-out;
  margin-bottom: 12px;
  cursor: pointer;
}

/* Very small screens: further reduce padding */
@media (max-width: 480px) {
  .row {
    padding: 12px;
  }
}

/* Override forms.css rule that sets flex: 1 1 240px on .row > div */
.row > div {
  flex: 0 0 auto;
  min-width: 0;
}

.row:hover {
  border-color: #4b5563; /* hover:border-gray-600 */
}

/* First line: Header with title and status */
.task-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.task-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.task-title-wrapper {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.task-title-wrapper.expanded {
  overflow: visible;
}

.task-title {
  display: block;
  font-weight: 600;
  color: inherit;
  font-size: 15px;
  line-height: 1.4;
}

.task-title.title-truncated {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-title-wrapper.expanded .task-title {
  white-space: normal;
  overflow: visible;
}

.title-expand-btn {
  flex-shrink: 0;
}

.title-expand-btn svg.rotated {
  transform: rotate(180deg);
}

.task-status-section {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.status-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-name {
  font-size: 14px;
  white-space: nowrap;
}

.transition-btn {
  background-color: #fdfdfd !important;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.transition-btn:hover:not(:disabled) {
  opacity: 1;
  background-color: #fff !important;
  box-shadow:
    0 2px 2px -1px rgba(42, 171, 238, 0.3),
    0 2px 2px -1px rgba(42, 171, 238, 0.3);
}

.transition-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Second line: Content columns */
.task-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

.content-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  min-height: 0;
}

.task-meta-text {
  display: block;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.task-meta-project-role {
  font-size: 0.9em;
  color: #9e9e9e;
  font-style: italic;
  margin-left: 4px;
}

/* Third and Fourth lines: Labels */
.labels-container {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.personal-labels {
  margin-top: 4px;
}

.project-labels {
  margin-top: 4px;
}

.label-badge {
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  transition: opacity 0.2s ease;
}

.archived-label {
  background-color: #6c757d;
  color: #fff;
}

.personal-label-badge {
  border: 1px dashed rgba(255, 255, 255, 0.5);
}

.archived-task {
  opacity: 0.6;
  background-color: #f3f4f6;
}

.archived-task .muted {
  color: #6c757d;
}

/* Small screens: single column for content, reduced padding */
@media (max-width: 768px) {
  .row {
    padding: 16px;
    margin-bottom: 10px;
  }

  .task-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .task-title-section {
    order: 2;
    width: 100%;
  }

  .task-status-section {
    order: 1;
    width: 100%;
  }

  .task-content {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

/* Very small screens: further reduce padding */
@media (max-width: 480px) {
  .row {
    padding: 12px;
    margin-bottom: 8px;
  }
}
</style>
